import React, {
  JSX,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import FocusTrap from 'focus-trap-react';
import GetViewportSize from '../../assets/utils/getViewportSize';
import { Image, Link, Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import Icons from 'components/atoms/Icons';
import { classNames } from 'assets/utils/helper';
import {
  SubLinkItem,
  OSFHeaderProps,
  LinkItem,
  InternalLinksItem,
} from 'components/types/OSFHeaderTypes';
import OSFNavSubLinks from 'components/organisms/OSFNavSubLinks';
import {
  useSitecoreContext,
  SitecoreContextValue,
} from '@sitecore-jss/sitecore-jss-nextjs';
import ThemeContext from 'components/context/ThemeContext';
import PrimarySubNav from 'components/molecules/PrimarySubNav';
//import GlobalSearchBar from 'components/atoms/GlobalSearchbar';

type CustomSitecoreContextType = {
  sitecoreContext: SitecoreContextValue & {
    itemPath?: string;
  };
};

const OSFHeader = (props: OSFHeaderProps): JSX.Element => {
  const { fields } = props;
  const ref = useRef<HTMLElement>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeSubNav, setActiveSubNav] = useState('');
  const [activeColumn, setActiveColumn] = useState<number | null>(null);
  const [activeSubColumn, setActiveSubColumn] = useState<number | null>(null);
  const [activeSubLinks, setActiveSubLinks] = useState<number | null>(null);
  const [busnType, setBusnType] = useState('');
  const [path, setPath] = useState('');
  const { sitecoreContext }: CustomSitecoreContextType = useSitecoreContext();
  const isHomePage = sitecoreContext?.itemPath == '/';
  const isProviderPage = sitecoreContext?.itemPath?.includes('/Providers/');
  const isLocationPage = sitecoreContext?.itemPath?.includes('/Locations/');
  const [viewportSize, setViewportSize] = useState<string | null>(null);
  const theme = useContext(ThemeContext)?.themeState?.theme;
  const mobileRef = useRef(false);
  // const [headerSearchOpen, setHeaderSearchOpen] = useState(false);

  useEffect(() => {
    setViewportSize(GetViewportSize());
    const handleResize = () => setViewportSize(GetViewportSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const header = ref.current;
    const plac = header?.parentElement;
    if (!header || !plac) return;

    const syncHeaderSpace = () => {
      plac.style.setProperty('--osf-header-space', `${header.offsetHeight}px`);
    };

    syncHeaderSpace();
    const observer = new ResizeObserver(syncHeaderSpace);
    observer.observe(header);
    return () => observer.disconnect();
  }, [viewportSize]);

  useEffect(() => {
    closeMobileNav();
    if (sitecoreContext.itemPath?.includes('/oncall/business-solutions')) {
      setBusnType('b2b');
      setPath('busn-sol');
    } else if (sitecoreContext.itemPath?.includes('/oncall/urgent-care')) {
      setBusnType('b2c');
      setPath('urg-care');
    } else if (sitecoreContext.itemPath?.includes('/oncall/connect')) {
      setBusnType('b2c');
      setPath('connect');
    } else if (sitecoreContext.itemPath?.includes('/oncall')) {
      setBusnType('b2c');
      setPath('oncall');
    }
  }, [sitecoreContext]);

  const closeMobileNav = useCallback(() => {
    const mobileNavEl = ref?.current?.querySelector('.nav-menu');
    if (mobileNavEl) {
      mobileNavEl.classList.remove('mobile-nav-open');
      mobileNavEl.classList.add('closing');
    }

    const subNavMenus = ref?.current?.querySelectorAll('.sub-nav-menu');
    subNavMenus?.forEach(menu => {
      (menu as HTMLElement).classList.remove('active');
    });
    setActiveSubNav('');
    setActiveColumn(null);
    setActiveSubColumn(null);

    setTimeout(() => {
      setMobileNavOpen(false);
      setActiveSubNav('');
      setActiveColumn(null);
      setActiveSubColumn(null);

      if (mobileNavEl) {
        (mobileNavEl as HTMLElement).classList.remove('closing');
      }
      if (mobileRef.current === true) {
        const scrollY = document.body.style.top;
        document.body.classList.remove('body-fixed');
        document.body.style.removeProperty('top');
        window.scroll(0, parseInt(scrollY || '0') * -1);
        mobileRef.current = false;
      }
    }, 50);
  }, [
    ref,
    setMobileNavOpen,
    setActiveSubNav,
    setActiveColumn,
    setActiveSubColumn,
  ]);

  if (viewportSize === null || !fields) {
    return (
      <header
        className="osf-header-nav"
        ref={ref}
        id="osf-header-root"
        tabIndex={-1}
      >
        <div
          className={classNames(
            'overhauled header-border',
            theme === 'sacon' || theme === 'sfcon'
              ? 'bg-primary-dark'
              : theme === 'sisters'
              ? 'gradient-bg-sisters-primary'
              : 'gradient-horizontal'
          )}
        ></div>
      </header>
    );
  }

  const groupSubLinks = (
    internalLinks: InternalLinksItem[] | undefined,
    activeSubNavId: string
  ) => {
    const activeItem = internalLinks?.find(item => item.id === activeSubNavId);

    if (!activeItem || !activeItem.fields.SubLink) return [];

    return activeItem.fields.SubLink.reduce(
      (
        acc: Array<{ heading: string; items: SubLinkItem[] }>,
        subLink: SubLinkItem
      ) => {
        if (subLink.fields.heading?.value) {
          acc.push({
            heading: subLink.fields.heading.value,
            items: [subLink],
          });
        } else if (subLink.displayName && !subLink.fields.heading?.value) {
          subLink.fields.lists?.forEach(listItem => {
            acc.push({
              heading: '',
              items: [listItem],
            });
          });
        } else if (acc.length > 0) {
          acc[acc.length - 1].items.push(subLink);
        }
        return acc;
      },
      [] as Array<{ heading: string; items: SubLinkItem[] }>
    );
  };

  const groupedSubLinks = groupSubLinks(fields?.internalLinks, activeSubNav);

  const openMobileNav = () => {
    mobileRef.current = true;
    const topOffset = `-${window.pageYOffset}px`;
    document.body.classList.add('body-fixed');
    document.body.style.top = topOffset;
    setMobileNavOpen(true);
  };

  const openSubNav = (itemId: string) => {
    setActiveSubNav(itemId);

    const internalLinkItem = fields.internalLinks?.find(
      (item: InternalLinksItem) => item.id === itemId
    );

    if (internalLinkItem && internalLinkItem.fields.SubLink) {
      const subLinks = internalLinkItem.fields.SubLink;
      const uniqueHeading = new Set(
        subLinks
          .map((link: SubLinkItem) => link.fields.heading?.value)
          .filter(Boolean)
      );

      if (uniqueHeading.size === 1) {
        setActiveColumn(0);
        setActiveSubColumn(0);
      } else {
        setActiveColumn(null);
        setActiveSubColumn(null);
      }
    } else {
      setActiveColumn(null);
      setActiveSubColumn(null);
    }
  };

  const featuredItems = fields.internalLinks?.filter(
    item => item.fields.BaseSvg?.value
  );
  const listItems = mobileNavOpen
    ? fields.internalLinks?.filter(item => !item.fields.BaseSvg?.value)
    : fields.internalLinks;

  const homeHref = fields.homeUrl?.value?.href || '/';
  const homeUrlField = fields.homeUrl?.value?.href
    ? fields.homeUrl
    : { value: { href: '/' } };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.location.href = homeHref;
  };
  return (
    <header
      className={
        isHomePage || isLocationPage || isProviderPage
          ? 'osf-header-nav home'
          : 'osf-header-nav'
      }
      ref={ref}
      id="osf-header-root"
      tabIndex={-1}
    >
      <div
        className={classNames(
          'overhauled header-border',
          theme === 'sacon' || theme === 'sfcon'
            ? 'bg-primary-dark'
            : theme === 'sisters'
              ? 'gradient-bg-sisters-primary'
              : busnType == 'b2b'
                ? 'gradient-bg-oncall-b2b-horizontal'
                : 'gradient-horizontal'
        )}
      ></div>
      {!isHomePage &&
        !isLocationPage &&
        !isProviderPage &&
        fields.links?.[0] && (
          <div className="back-button">
            <span className="icon icon-chevron-down">
              <Icons id="chevron-down" />
            </span>
            <Link
              field={fields.links[0].fields.BaseLink}
              className="back-button-link"
            >
              {fields.links[0].fields.BaseLink.value.text}
            </Link>
          </div>
        )}
      <div className="nav-container">
        <div className="navbar">
          {!isHomePage &&
            !isLocationPage &&
            !isProviderPage &&
            fields.links?.[0] && (
              <div className="back-button">
                <span className="icon icon-chevron-down">
                  <Icons id="chevron-down" />
                </span>
                <Link
                  field={fields.links[0].fields.BaseLink}
                  className="back-button-link"
                >
                  {fields.links[0].fields.BaseLink.value.text}
                </Link>
              </div>
            )}
          <div className="osf-logo nav-logo">
            <Link field={homeUrlField} onClick={handleLogoClick}>
              {busnType ? (
                <Image
                  field={{
                    value: {
                      src:
                        path == 'busn-sol'
                          ? 'https://osf-p-001.sitecorecontenthub.cloud/api/public/content/a680b43d5d66498f8017650d185bf53e?v=95bd412a'
                          : path == 'urg-care'
                            ? 'https://osf-p-001.sitecorecontenthub.cloud/api/public/content/6ff0b985e52f405cb48b14662c8367b9?v=8a20aef4'
                            : path == 'connect'
                              ? 'https://osf-p-001.sitecorecontenthub.cloud/api/public/content/11c03a951f604a1a8ab1917b3311891d?v=b56ac8a0'
                              : 'https://osf-p-001.sitecorecontenthub.cloud/api/public/content/1c957742499346908bc52346a4df739a?v=e6ecd849',
                    },
                  }}
                  fill
                  alt="OSF HealthCare"
                ></Image>
              ) : (
                <Image field={fields.logo} alt="OSF HealthCare" />
              )}
            </Link>
            <button
              className="menu-open"
              onClick={openMobileNav}
              aria-label="Open Menu"
            >
              <span className="icon icon-menu">
                <Icons id="menu-three" />
              </span>
            </button>
          </div>
        </div>
        <FocusTrap
          active={mobileNavOpen}
          focusTrapOptions={{
            escapeDeactivates: false,
            delayInitialFocus: true,
            initialFocus: () =>
              (document.querySelector('.menu-close') as HTMLElement) ||
              (document.getElementById('osf-header-root') as HTMLElement),
            fallbackFocus: '#osf-header-root',
            returnFocusOnDeactivate: true,
            onDeactivate: () => {
              (
                document.getElementById('osf-header-root') as HTMLElement | null
              )?.focus();
            },
            checkCanFocusTrap: () =>
              new Promise<void>(resolve =>
                requestAnimationFrame(() => resolve())
              ),
          }}
        >
          <nav
            aria-hidden={
              viewportSize !== 'md' &&
              viewportSize !== 'lg' &&
              viewportSize !== 'xl' &&
              !mobileNavOpen
            }
            aria-expanded={
              viewportSize !== 'md' &&
              viewportSize !== 'lg' &&
              viewportSize !== 'xl' &&
              mobileNavOpen
            }
            className={`nav-menu ${mobileNavOpen ? 'mobile-nav-open' : ''}`}
          >
            <div
              className={classNames(
                'overhauled header-border',
                theme === 'sacon' || theme === 'sfcon'
                  ? 'bg-primary-dark'
                  : theme === 'sisters'
                    ? 'gradient-bg-sisters-primary'
                    : busnType === 'b2b'
                      ? 'gradient-bg-oncall-b2b-horizontal'
                      : 'gradient-horizontal'
              )}
            ></div>
            <div className="nav-header">
              {fields.links?.[0] && mobileNavOpen && (
                <div className="back-button">
                  <span className="icon icon-chevron-down">
                    <Icons id="chevron-down" />
                  </span>
                  <Link
                    field={fields.links[0].fields.BaseLink}
                    className="back-button-link"
                  >
                    {fields.links[0].fields.BaseLink.value.text}
                  </Link>
                </div>
              )}
              <div className="osf-logo">
                <Link field={homeUrlField} onClick={handleLogoClick}>
                  {busnType ? (
                    <Image
                      field={{
                        value: {
                          src:
                            path == 'busn-sol'
                              ? 'https://osf-p-001.sitecorecontenthub.cloud/api/public/content/a680b43d5d66498f8017650d185bf53e?v=95bd412a'
                              : path == 'urg-care'
                                ? 'https://osf-p-001.sitecorecontenthub.cloud/api/public/content/6ff0b985e52f405cb48b14662c8367b9?v=8a20aef4'
                                : path == 'connect'
                                  ? 'https://osf-p-001.sitecorecontenthub.cloud/api/public/content/11c03a951f604a1a8ab1917b3311891d?v=b56ac8a0'
                                  : 'https://osf-p-001.sitecorecontenthub.cloud/api/public/content/1c957742499346908bc52346a4df739a?v=e6ecd849',
                        },
                      }}
                      fill
                      alt="OSF HealthCare"
                    ></Image>
                  ) : (
                    <Image field={fields.logo} alt="OSF HealthCare" />
                  )}
                </Link>
              </div>
              <button
                className="menu-close"
                onClick={closeMobileNav}
                aria-label="Close Menu"
              >
                <span className="">
                  <Icons id="close-header" />
                </span>
              </button>
              <div
                className={classNames(
                  'overhauled mobile-border',
                  theme === 'sacon' || theme === 'sfcon'
                    ? 'bg-primary-dark'
                    : theme === 'sisters'
                      ? 'gradient-bg-sisters-primary'
                      : busnType === 'b2b'
                        ? 'gradient-bg-oncall-b2b-horizontal'
                        : 'gradient-horizontal'
                )}
              ></div>
            </div>
            <div className="nav-content">
              {mobileNavOpen && (
                <div className="featured-items">
                  {featuredItems?.map(item => {
                    return (
                      <div
                        key={item.id}
                        className={classNames(
                          'nav-action overhauled featured-item-wrapper',
                          item.fields.SubLink?.length ? 'has-sub-nav' : '',
                          activeSubNav === item.id && mobileNavOpen
                            ? 'active'
                            : ''
                        )}
                      >
                        {!item.fields.SubLink?.length ||
                        item.fields?.BasePhoneNumber?.value ? (
                          <a
                            key={item.id}
                            href={
                              item.fields?.BasePhoneNumber?.value
                                ? `tel:${item.fields?.BasePhoneNumber?.value}`
                                : item.fields?.BaseLink?.value?.href
                            }
                            target={item.fields?.BaseLink?.value?.target}
                            className="featured-item"
                          >
                            <span className="featured-icon">
                              <Icons
                                id={item.fields.BaseSvg?.value}
                                removeSize={true}
                              />
                            </span>
                            <span className="featured-text">
                              {item.fields?.BasePhoneNumber?.value ||
                                item.fields.BaseLink.value.text ||
                                item.displayName ||
                                item.name}
                            </span>
                          </a>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                if (mobileNavOpen) openSubNav(item.id);
                              }}
                              className={classNames('featured-item')}
                              aria-label={
                                item.fields.BaseLink.value.text ||
                                item.displayName ||
                                item.name
                              }
                            >
                              <span className="featured-icon">
                                <Icons
                                  id={item.fields.BaseSvg?.value}
                                  removeSize={true}
                                />
                              </span>
                              <span className="featured-text">
                                {item.fields.BaseLink.value.text ||
                                  item.displayName ||
                                  item.name}
                              </span>
                            </button>

                            <div
                              className={`sub-nav-menu ${activeSubNav === item.id ? 'active' : ''}`}
                              id={`sub-nav-${item.id}`}
                              tabIndex={-1}
                            >
                              {item.fields?.SubLink &&
                                item.fields?.SubLink?.length > 0 && (
                                  <OSFNavSubLinks
                                    subLinks={item.fields.SubLink}
                                    maxColumnRows={parseInt(
                                      item.fields?.maxColumnRows?.value
                                    )}
                                    columnNumber={parseInt(
                                      item.fields?.columnNumber?.value
                                    )}
                                    activeColumn={activeColumn}
                                    setActiveColumn={setActiveColumn}
                                    activeSubColumn={activeSubColumn}
                                    setActiveSubColumn={setActiveSubColumn}
                                    groupedSubLinks={groupedSubLinks}
                                    item={item}
                                    setActiveSubNav={setActiveSubNav}
                                  />
                                )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="main-nav">
                {listItems?.map(item => {
                  if (mobileNavOpen && item.fields.BaseSvg?.value) {
                    return null;
                  }
                  return (
                    <div
                      key={item.id}
                      className={classNames(
                        'nav-action overhauled',
                        item.fields.SubLink?.length ? 'has-sub-nav' : '',
                        activeSubNav === item.id && mobileNavOpen
                          ? 'active'
                          : ''
                      )}
                      onMouseEnter={() => {
                        if (!mobileNavOpen) openSubNav(item.id);
                      }}
                      onMouseLeave={() => {
                        if (!mobileNavOpen) setActiveSubNav('');
                      }}
                    >
                      {!item.fields.SubLink?.length ? (
                        <a
                          href={
                            item.fields?.BasePhoneNumber?.value
                              ? `tel:${item.fields?.BasePhoneNumber?.value}`
                              : item.fields?.BaseLink?.value?.href
                          }
                          target={item.fields?.BaseLink?.value?.target}
                          className={classNames(
                            Boolean(
                              item.fields.BackgroundColor?.fields.Phrase.value
                            )
                              ? 'btn-primary'
                              : 'nav-action-btn'
                          )}
                          style={
                            item.fields.BackgroundColor
                              ? {
                                  backgroundColor:
                                    item.fields.BackgroundColor?.fields.Phrase
                                      .value,
                                }
                              : {}
                          }
                        >
                          {item.fields?.BasePhoneNumber?.value ||
                            item.fields.BaseLink.value.text ||
                            item.displayName ||
                            item.name}
                        </a>
                      ) : (
                        <>
                          {item.fields?.BasePhoneNumber?.value ? (
                            <a
                              className="nav-action-btn"
                              href={`tel:${item.fields?.BasePhoneNumber?.value}`}
                            >
                              {item.fields?.BasePhoneNumber?.value}
                            </a>
                          ) : (
                            <button
                              onClick={() => {
                                if (mobileNavOpen) openSubNav(item.id);
                              }}
                              className={classNames('nav-action-btn')}
                            >
                              {item.fields.BaseLink.value.text ||
                                item.displayName ||
                                item.name}
                              {mobileNavOpen && (
                                <span className="chevron-right-icon">
                                  <Icons id="chevron-right-icon" />
                                </span>
                              )}
                            </button>
                          )}
                          <FocusTrap
                            active={activeSubNav === item.id && mobileNavOpen}
                            focusTrapOptions={{
                              escapeDeactivates: false,
                              allowOutsideClick: true,
                              clickOutsideDeactivates: false,
                              fallbackFocus: `#sub-nav-${item.id}`,
                              returnFocusOnDeactivate: true,
                              onDeactivate: () => {
                                (
                                  document.getElementById(
                                    'osf-header-root'
                                  ) as HTMLElement | null
                                )?.focus();
                              },
                            }}
                          >
                            <div
                              className={`sub-nav-menu ${activeSubNav === item.id ? 'active' : ''}`}
                              id={`sub-nav-${item.id}`}
                              tabIndex={-1}
                            >
                              {item.fields?.SubLink &&
                                item.fields?.SubLink?.length > 0 && (
                                  <OSFNavSubLinks
                                    subLinks={item.fields.SubLink}
                                    maxColumnRows={parseInt(
                                      item.fields?.maxColumnRows?.value
                                    )}
                                    columnNumber={parseInt(
                                      item.fields?.columnNumber?.value
                                    )}
                                    activeColumn={activeColumn}
                                    setActiveColumn={setActiveColumn}
                                    activeSubColumn={activeSubColumn}
                                    setActiveSubColumn={setActiveSubColumn}
                                    groupedSubLinks={groupedSubLinks}
                                    item={item}
                                    setActiveSubNav={setActiveSubNav}
                                  />
                                )}
                            </div>
                          </FocusTrap>
                        </>
                      )}
                    </div>
                  );
                })}
                <Placeholder
                  name="header-modal-btn"
                  rendering={props.rendering}
                ></Placeholder>
              </div>
              <div className="nav-utility">
                <div className="utility-links">
                  {fields?.links
                    ?.slice(1)
                    .map((link: LinkItem, index: number) =>
                      link?.fields?.UtilitySubLink?.length > 0 ? (
                        <PrimarySubNav
                          setActiveSubLinks={setActiveSubLinks}
                          activeSubLinks={activeSubLinks}
                          index={index}
                          link={link}
                        ></PrimarySubNav>
                      ) : (
                        <a
                          key={link.id}
                          href={
                            link.fields?.BasePhoneNumber?.value
                              ? `tel:${link.fields?.BasePhoneNumber?.value}`
                              : link.fields?.BaseLink?.value?.href
                          }
                          target={link.fields?.BaseLink?.value?.target}
                          className="utility-link"
                        >
                          {link.fields?.svg?.value && (
                            <span className="icon icon-location">
                              <Icons id={link.fields?.svg?.value} />
                            </span>
                          )}
                          {link.fields?.BasePhoneNumber?.value ||
                            link.displayName}
                        </a>
                      )
                    )}
                  {/*  Global search icon hidden for production release */}
                  {/*<button
                    type="button"
                    className="utility-link search-icon-trigger"
                    aria-label="Search"
                    aria-expanded={headerSearchOpen}
                    onClick={() => setHeaderSearchOpen(prev => !prev)}
                  >
                    <span className="icon icon-search">
                      <Icons id="icon-search-green" />
                    </span>
                  </button>*/}
                </div>
              </div>
            </div>
          </nav>
        </FocusTrap>
      </div>

      {/* {headerSearchOpen && (
        <div className="global-search-dropdown">
          <GlobalSearchBar onClose={() => setHeaderSearchOpen(false)} />
        </div>
      )}*/}
    </header>
  );
};

export default OSFHeader;
